import { useState, useEffect } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useFetch } from "../../lib/useData";
import { PageTitle, Btn, Input, Textarea } from "../../components/admin/ui";

export default function SettingsAdmin() {
  const { data } = useFetch("/admin/settings");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data && !form) setForm(data); }, [data]); // eslint-disable-line
  if (!form) return <p className="text-slate-400">Loading…</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (parent, k, v) => set(parent, { ...(form[parent] || {}), [k]: v });

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", form); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const Card = ({ title, children }) => (
    <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5 space-y-4">
      <h3 className="font-semibold">{title}</h3>{children}
    </div>
  );

  return (
    <div>
      <PageTitle title="Settings" desc="Temple information, payment, policies & SEO">
        <Btn onClick={save} disabled={saving} testid="settings-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}</Btn>
      </PageTitle>
      <div className="grid md:grid-cols-2 gap-6">
        <Card title="Temple Information">
          <Input label="Temple Name" value={form.temple_name} onChange={(v) => set("temple_name", v)} testid="s-name" />
          <Input label="Tagline / Location" value={form.temple_tagline} onChange={(v) => set("temple_tagline", v)} />
          <Textarea label="Address" value={form.address} onChange={(v) => set("address", v)} rows={2} />
          <Input label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="s-phone" />
          <Input label="Email" value={form.email} onChange={(v) => set("email", v)} />
          <Input label="Logo URL" value={form.logo_url} onChange={(v) => set("logo_url", v)} />
        </Card>

        <Card title="Location & Maps">
          <Input label="Google Maps Embed URL" value={form.maps_embed_url} onChange={(v) => set("maps_embed_url", v)} />
          <Input label="Directions URL" value={form.maps_directions_url} onChange={(v) => set("maps_directions_url", v)} />
          <Input label="Facebook" value={form.social?.facebook} onChange={(v) => setNested("social", "facebook", v)} />
          <Input label="Instagram" value={form.social?.instagram} onChange={(v) => setNested("social", "instagram", v)} />
          <Input label="YouTube" value={form.social?.youtube} onChange={(v) => setNested("social", "youtube", v)} />
        </Card>

        <Card title="Payment Settings">
          <Input label="UPI ID" value={form.upi_id} onChange={(v) => set("upi_id", v)} testid="s-upi" />
          <Input label="QR Image URL" value={form.qr_image_url} onChange={(v) => set("qr_image_url", v)} />
          <Input label="Bank A/c Name" value={form.bank_details?.account_name} onChange={(v) => setNested("bank_details", "account_name", v)} />
          <Input label="Bank A/c Number" value={form.bank_details?.account_number} onChange={(v) => setNested("bank_details", "account_number", v)} />
          <Input label="IFSC" value={form.bank_details?.ifsc} onChange={(v) => setNested("bank_details", "ifsc", v)} />
          <Input label="Bank Name" value={form.bank_details?.bank} onChange={(v) => setNested("bank_details", "bank", v)} />
        </Card>

        <Card title="Booking Policy">
          <Input label="Cancellation Window (hours)" type="number" value={form.cancellation_window_hours} onChange={(v) => set("cancellation_window_hours", Number(v))} testid="s-cancel-window" />
          <Input label="Refund Percentage (%)" type="number" value={form.refund_percentage} onChange={(v) => set("refund_percentage", Number(v))} testid="s-refund-pct" />
          <Input label="Booking ID Prefix" value={form.booking_prefix} onChange={(v) => set("booking_prefix", v)} />
          <Input label="Currency Symbol" value={form.currency_symbol} onChange={(v) => set("currency_symbol", v)} />
        </Card>

        <Card title="SEO Defaults">
          <Input label="Meta Title" value={form.seo?.title} onChange={(v) => setNested("seo", "title", v)} />
          <Textarea label="Meta Description" value={form.seo?.description} onChange={(v) => setNested("seo", "description", v)} rows={2} />
          <Input label="Keywords" value={form.seo?.keywords} onChange={(v) => setNested("seo", "keywords", v)} />
        </Card>
      </div>
    </div>
  );
}
